/**
 * Utilities for enriching Sentry error events with structured metadata.
 *
 * These helpers are used by Axios response interceptors to add per-endpoint
 * fingerprinting and URL tags so that 5xx errors group into separate Sentry
 * issues (one per endpoint) rather than collapsing into a single generic issue.
 */

/**
 * Normalizes an API URL path for Sentry fingerprinting and tags.
 *
 * Transformation rules (in order):
 *  1. Extract the pathname from absolute URLs; treat relative strings as paths.
 *  2. Strip query parameters.
 *  3. Replace every numeric path segment with `:id` so that
 *     `/invoice/123/line-items` and `/invoice/456/line-items` share the same
 *     fingerprint component.
 *  4. Truncate to 200 characters.
 *  5. Return `'unknown'` for falsy input or any parse error.
 *
 * @param {string|undefined|null} url - The request URL (relative or absolute).
 * @returns {string} Normalized path suitable for Sentry fingerprint / tags.
 */
export function normalizeSentryPath(url) {
    if (!url) return 'unknown';

    try {
        let path;

        if (/^https?:\/\//i.test(url)) {
            // Absolute URL — extract only the pathname.
            path = new URL(url).pathname;
        } else {
            // Relative URL — strip query string and hash.
            path = url.split('?')[0].split('#')[0];
        }

        // Replace numeric-only path segments (pure digits) with :id.
        path = path.replace(/\/\d+(?=\/|$)/g, '/:id');

        return path.slice(0, 200);
    } catch {
        return 'unknown';
    }
}
